<!-- src/components/common/BaseModal.vue -->
<template>
  <div
    class="modal fade"
    :id="id"
    tabindex="-1"
    :aria-labelledby="`${id}Label`"
    aria-hidden="true"
    ref="modalElement"
  >
    <div class="modal-dialog modal-dialog-centered"  :class="[sizeClass, dialogClass]"  role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h3 class="modal-title" :id="`${id}Label`">{{ title }}</h3>
          <button
            class="btn-close py-0"
            type="button"
            data-bs-dismiss="modal"
            aria-label="Close"
            @click="$emit('close')"
          ></button>
        </div>
        <div class="modal-body">
          <!-- Slot for the main content -->
          <slot name="body"></slot>
        </div>
        <div class="modal-footer">
          <button
            v-if="showCloseButton"
            class="btn btn-secondary"
            type="button"
            data-bs-dismiss="modal"
            @click="$emit('close')"
          >
            {{ closeButtonText }}
          </button>
          <button
            v-if="showSaveButton"
            class="btn save_btn"
            :class="saveButtonClass"
            type="button"
            @click="$emit('save')"
            :disabled="saveButtonDisabled"
          >
            {{ saveButtonText }}
          </button>
          <!-- Slot for additional footer buttons -->
          <slot name="footer"></slot>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent, computed } from 'vue';
export default defineComponent({
  name: 'BaseModal'
});
</script>
<script lang="ts" setup>
import { ref, onMounted, onUnmounted, watch } from 'vue';

const props = withDefaults(defineProps<{
  id: string; // Unique ID for the modal
  title: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  dialogClass?: string; // For custom dialog sizing (e.g., 'modal-lg')
  showCloseButton?: boolean;
  closeButtonText?: string;
  showSaveButton?: boolean;
  saveButtonText?: string;
  saveButtonClass?: string;
  saveButtonDisabled?: boolean;
  visible?: boolean; // Control visibility from parent
}>(), {
  size:'md' , 
  dialogClass: '',
  showCloseButton: true,
  closeButtonText: 'Close',
  showSaveButton: true,
  saveButtonText: 'Save changes',
  saveButtonClass: 'btn-primary',
  saveButtonDisabled: false,
  visible: false
});

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'save'): void;
  (e: 'shown'): void; // Emitted when modal is fully shown
  (e: 'hidden'): void; // Emitted when modal is fully hidden
}>();

const sizeClass = computed(() => {
  switch (props.size) {
    case 'sm':
      return 'modal-sm';
    case 'lg':
      return 'modal-lg';
    case 'xl':
      return 'modal-xl';
    case 'full':
      return 'modal-fullscreen';
    case 'md':
    default:
      return '';
  }
});

const modalElement = ref<HTMLElement | null>(null);
let bootstrapModalInstance: any = null;

const initBootstrapModal = () => {
  const bootstrap = (window as any).bootstrap;
  if (bootstrap && bootstrap.Modal && modalElement.value) {
     // Ensure only one instance exists
    if (bootstrapModalInstance) {
        bootstrapModalInstance.dispose();
    }
    bootstrapModalInstance = new bootstrap.Modal(modalElement.value, {
      backdrop: 'static', // Optional: prevent closing by clicking backdrop
      keyboard: false     // Optional: prevent closing by ESC key
    });

    // Listen to Bootstrap's native events
    modalElement.value.addEventListener('shown.bs.modal', () => emit('shown'));
    modalElement.value.addEventListener('hidden.bs.modal', () => emit('hidden'));
  }
};

const show = () => {
  if (bootstrapModalInstance) {
    bootstrapModalInstance.show();
  } else {
    // Fallback if Bootstrap JS fails or isn't ready yet
    if (modalElement.value) {
       modalElement.value.classList.add('show');
       modalElement.value.style.display = 'block';
       document.body.classList.add('modal-open');
       if (!document.querySelector('.modal-backdrop')) {
         const backdrop = document.createElement('div');
         backdrop.className = 'modal-backdrop fade show';
         document.body.appendChild(backdrop);
       }
       emit('shown'); // Emit shown event for fallback
    }
  }
};

const hide = () => {
  if (bootstrapModalInstance) {
    bootstrapModalInstance.hide();
  } else {
    // Fallback hiding
    if (modalElement.value) {
       modalElement.value.classList.remove('show');
       modalElement.value.style.display = 'none';
       document.body.classList.remove('modal-open');
       const backdrop = document.querySelector('.modal-backdrop');
       if (backdrop) backdrop.remove();
       emit('hidden'); // Emit hidden event for fallback
    }
  }
};

// Watch for visibility changes from parent
watch(() => props.visible, (newVal) => {
  if (newVal) {
    show();
  } else {
    hide();
  }
});

onMounted(() => {
   initBootstrapModal();
   // If visible prop is true on mount, show the modal
   if (props.visible) {
       show();
   }
});

onUnmounted(() => {
    if (bootstrapModalInstance) {
        bootstrapModalInstance.dispose();
        bootstrapModalInstance = null;
    }
});

// Expose methods to parent components
defineExpose({ show, hide });

</script>

<style scoped>
/* Add any specific styles if needed */

button.save_btn{
  background-color: #63d5be;
}
</style>